/**
 * Analytics & Insights (Phase 1) -- reusable frontend tracking utility plus
 * the Admin Analytics & Insights query calls. Mirrors backend
 * `app.schemas.analytics.*` / `app.api.v1.admin_analytics` field-for-field.
 *
 * `trackEvent()` is fire-and-forget by design: analytics must never
 * disturb the main application. It swallows its own errors (network
 * failure, backend down, disabled flag, etc.) rather than throwing, and
 * nothing that calls it should ever `await` it for correctness. Posts to
 * `POST /analytics/events`, which is deliberately auth-optional on the
 * backend (see `app.api.v1.analytics`) so pre-login events
 * (`login_failed`, `signup_completed`) still record.
 */
import api from "@/services/api";
import type {
  AnalyticsApiHealthResponse,
  AnalyticsDateRangeParams,
  AnalyticsEventCategory,
  AnalyticsEventsResponse,
  AnalyticsFeaturesResponse,
  AnalyticsOverviewResponse,
  AnalyticsSessionsResponse,
  AnalyticsUsersResponse,
} from "@/types";

const SESSION_ID_KEY = "tekinvest_analytics_session_id";

function getOrCreateSessionId(): string {
  let sessionId = sessionStorage.getItem(SESSION_ID_KEY);
  if (!sessionId) {
    sessionId =
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    sessionStorage.setItem(SESSION_ID_KEY, sessionId);
  }
  return sessionId;
}

export interface TrackEventOptions {
  eventCategory?: AnalyticsEventCategory;
  metadata?: Record<string, unknown> | null;
}

/**
 * Fire-and-forget event tracking -- never throws, never blocks the caller.
 * Do NOT put passwords, tokens, broker credentials, financial orders, or
 * other sensitive personal data in `metadata` (see backend
 * `AnalyticsEvent.event_metadata` docstring).
 */
export function trackEvent(eventName: string, options: TrackEventOptions = {}): void {
  const { eventCategory = "APPLICATION", metadata = null } = options;
  const payload = {
    event_name: eventName,
    event_category: eventCategory,
    session_id: getOrCreateSessionId(),
    page_path: window.location.pathname,
    metadata,
  };
  api.post("/analytics/events", payload).catch((err) => {
    // Deliberately swallowed -- see module docstring.
    console.debug("[analytics] trackEvent failed (non-fatal):", err);
  });
}

function toQueryParams(params: AnalyticsDateRangeParams = {}) {
  return {
    range: params.range ?? "7d",
    from_date: params.from_date,
    to_date: params.to_date,
  };
}

export const analyticsService = {
  trackEvent,
  getOverview: async (params?: AnalyticsDateRangeParams): Promise<AnalyticsOverviewResponse> => {
    const { data } = await api.get<AnalyticsOverviewResponse>("/admin/analytics/overview", {
      params: toQueryParams(params),
    });
    return data;
  },
  getUsers: async (params?: AnalyticsDateRangeParams): Promise<AnalyticsUsersResponse> => {
    const { data } = await api.get<AnalyticsUsersResponse>("/admin/analytics/users", {
      params: toQueryParams(params),
    });
    return data;
  },
  getEvents: async (params?: AnalyticsDateRangeParams): Promise<AnalyticsEventsResponse> => {
    const { data } = await api.get<AnalyticsEventsResponse>("/admin/analytics/events", {
      params: toQueryParams(params),
    });
    return data;
  },
  getFeatures: async (params?: AnalyticsDateRangeParams): Promise<AnalyticsFeaturesResponse> => {
    const { data } = await api.get<AnalyticsFeaturesResponse>("/admin/analytics/features", {
      params: toQueryParams(params),
    });
    return data;
  },
  getSessions: async (params?: AnalyticsDateRangeParams): Promise<AnalyticsSessionsResponse> => {
    const { data } = await api.get<AnalyticsSessionsResponse>("/admin/analytics/sessions", {
      params: toQueryParams(params),
    });
    return data;
  },
  getApiHealth: async (params?: AnalyticsDateRangeParams): Promise<AnalyticsApiHealthResponse> => {
    const { data } = await api.get<AnalyticsApiHealthResponse>("/admin/analytics/api-health", {
      params: toQueryParams(params),
    });
    return data;
  },
};

export default analyticsService;
